import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  IconPlus,
  IconDatabaseOff,
  IconRefresh,
  IconPencil,
  IconTrash,
} from '@tabler/icons-react';
import type { TaskListCatalog, TaskListDefinition } from '@techgitter/contracts';
import { CreateTaskListDialog } from './CreateTaskListDialog';
import { TaskListDetailEditor } from './TaskListDetailEditor';
import { EditorModeToggle } from '../admin/EditorModeToggle';
import { SecondaryToolbar, ToolbarSpacer, ToolbarButton, ToolbarSearch } from '../toolbar';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '../table';
import type { EditorMode } from '../../hooks/useJsonGuiToggle';

interface TaskListGuiEditorProps {
  mode: EditorMode;
  onModeChange: (mode: EditorMode) => void;
  catalog: TaskListCatalog;
  onCatalogChange: (catalog: TaskListCatalog) => void;
  onSave: () => void;
  onClearCache: () => void;
  onReload: () => void;
  saving: boolean;
  clearing: boolean;
}

export const TaskListGuiEditor: React.FC<TaskListGuiEditorProps> = ({
  mode,
  onModeChange,
  catalog,
  onCatalogChange,
  onSave,
  onClearCache,
  onReload,
  saving,
  clearing,
}) => {
  const [editingTaskListId, setEditingTaskListId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TaskListDefinition | null>(null);
  const [filter, setFilter] = useState('');

  const editingTaskList = editingTaskListId
    ? catalog.taskLists.find((tl) => tl.taskListId === editingTaskListId) ?? null
    : null;

  const filteredTaskLists = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) return catalog.taskLists;
    return catalog.taskLists.filter((tl) =>
      [tl.taskListId, tl.displayName, tl.description]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query),
    );
  }, [catalog.taskLists, filter]);

  const handleCreate = (taskList: TaskListDefinition) => {
    onCatalogChange({
      ...catalog,
      taskLists: [...catalog.taskLists, taskList],
    });
    setCreateOpen(false);
    setEditingTaskListId(taskList.taskListId);
  };

  const handleTaskListChange = (updated: TaskListDefinition) => {
    onCatalogChange({
      ...catalog,
      taskLists: catalog.taskLists.map((tl) =>
        tl.taskListId === updated.taskListId ? updated : tl
      ),
    });
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    onCatalogChange({
      ...catalog,
      taskLists: catalog.taskLists.filter(
        (tl) => tl.taskListId !== deleteTarget.taskListId
      ),
    });
    setDeleteTarget(null);
  };

  const totalTasks = (tl: TaskListDefinition) =>
    tl.sections.reduce(
      (sum, s) => sum + ('tasks' in s ? s.tasks.length : 0),
      0,
    );

  // Detail view
  if (editingTaskList) {
    return (
      <TaskListDetailEditor
        taskList={editingTaskList}
        taskLists={catalog.taskLists}
        onChange={handleTaskListChange}
        onBack={() => setEditingTaskListId(null)}
        onSave={onSave}
        saving={saving}
      />
    );
  }

  // List view
  return (
    <>
      <SecondaryToolbar floating>
        <EditorModeToggle mode={mode} onChange={onModeChange} />
        <ToolbarSearch
          searchValue={filter}
          onSearchChange={setFilter}
          searchPlaceholder="Filter by ID, name, description…"
        />
        <ToolbarSpacer />
        <ToolbarButton variant="primary" icon={IconPlus} onClick={() => setCreateOpen(true)}>
          Create Task List
        </ToolbarButton>
        <ToolbarButton onClick={onSave}>
          {saving ? 'Saving…' : 'Save'}
        </ToolbarButton>
        <ToolbarButton icon={IconDatabaseOff} onClick={onClearCache}>
          {clearing ? 'Clearing…' : 'Clear Cache'}
        </ToolbarButton>
        <ToolbarButton icon={IconRefresh} onClick={onReload}>
          Reload
        </ToolbarButton>
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px' }}>
        <DataTableCard>
          <DataTable>
            <thead>
              <tr>
                <DataTableHeadCell>Task List ID</DataTableHeadCell>
                <DataTableHeadCell>Display Name</DataTableHeadCell>
                <DataTableHeadCell>Description</DataTableHeadCell>
                <DataTableHeadCell align="center">Sections</DataTableHeadCell>
                <DataTableHeadCell align="center">Tasks</DataTableHeadCell>
                <DataTableHeadCell align="right">Actions</DataTableHeadCell>
              </tr>
            </thead>
            <tbody>
              {filteredTaskLists.length === 0 ? (
                <DataTableEmpty colSpan={6}>
                  {filter.trim()
                    ? `No task lists match "${filter.trim()}".`
                    : 'No task lists. Click "Create Task List" to add one.'}
                </DataTableEmpty>
              ) : (
                filteredTaskLists.map((tl) => (
                  <DataTableRow key={tl.taskListId}>
                    <DataTableCell style={{ fontFamily: 'monospace' }}>{tl.taskListId}</DataTableCell>
                    <DataTableCell bold>{tl.displayName}</DataTableCell>
                    <DataTableCell muted>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 250, fontSize: 13 }}>
                        {tl.description || '—'}
                      </Typography>
                    </DataTableCell>
                    <DataTableCell align="center">{tl.sections.length}</DataTableCell>
                    <DataTableCell align="center">{totalTasks(tl)}</DataTableCell>
                    <DataTableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => setEditingTaskListId(tl.taskListId)} sx={{ color: '#888' }}>
                          <IconPencil size={16} stroke={1.5} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => setDeleteTarget(tl)} sx={{ color: '#888' }}>
                          <IconTrash size={16} stroke={1.5} />
                        </IconButton>
                      </Tooltip>
                    </DataTableCell>
                  </DataTableRow>
                ))
              )}
            </tbody>
          </DataTable>
        </DataTableCard>
      </Box>

      <CreateTaskListDialog
        open={createOpen}
        existingIds={catalog.taskLists.map((tl) => tl.taskListId)}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
      />

      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Task List</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{deleteTarget?.displayName}"?
            This cannot be undone after saving.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} sx={{ color: '#888' }}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disableElevation>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
